import { useEffect, useRef, useState, type ReactNode } from "react"
import { Check, Copy, Maximize2, Minimize2, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../Sheet/Sheet"

export interface CodeToken {
  content: string
  color?: string
}

export interface PublishedCodeDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  code: string
  title?: string
  subtitle?: string
  tokenizedLines?: CodeToken[][]
  /** Optional content to render in place of the read-only tokenized table (e.g. an inline editor on desktop). */
  bodySlot?: ReactNode
  /** Optional content rendered in the header before Copy/Expand/Close (e.g. Save/Discard/Publish buttons). */
  actionsSlot?: ReactNode
  /** Optional banner rendered between header and body (e.g. "Bot is running, stop before editing"). */
  bannerSlot?: ReactNode
  /** Optional footer slot rendered below the body. */
  footerSlot?: ReactNode
  mobileBreakpoint?: number
  dismissThresholdPx?: number
  copyAriaLabel?: string
  copiedAriaLabel?: string
  expandAriaLabel?: string
  collapseAriaLabel?: string
  closeAriaLabel?: string
  grabHandleAriaLabel?: string
  copyTimeoutMs?: number
  className?: string
}

function useIsBelow(bp: number) {
  const [below, setBelow] = useState(
    typeof window !== "undefined" ? window.innerWidth < bp : false,
  )
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${bp - 1}px)`)
    setBelow(mq.matches)
    const handler = (e: MediaQueryListEvent) => setBelow(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [bp])
  return below
}

export function PublishedCodeDrawer({
  open,
  onOpenChange,
  code,
  title = "Code",
  subtitle,
  tokenizedLines,
  bodySlot,
  actionsSlot,
  bannerSlot,
  footerSlot,
  mobileBreakpoint = 640,
  dismissThresholdPx = 40,
  copyAriaLabel = "Copy code",
  copiedAriaLabel = "Code copied",
  expandAriaLabel = "Expand to full screen",
  collapseAriaLabel = "Restore panel size",
  closeAriaLabel = "Close",
  grabHandleAriaLabel = "Drag down to close",
  copyTimeoutMs = 1500,
  className,
}: PublishedCodeDrawerProps) {
  const isMobile = useIsBelow(mobileBreakpoint)
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)
  const dragRef = useRef({ active: false, startY: 0 })

  useEffect(() => {
    if (!open) {
      setExpanded(false)
      setCopied(false)
    }
  }, [open])

  const onHandleDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId)
    dragRef.current = { active: true, startY: e.clientY }
  }
  const onHandleUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragRef.current.active) return
    const dy = e.clientY - dragRef.current.startY
    dragRef.current.active = false
    e.currentTarget.releasePointerCapture?.(e.pointerId)
    if (dy > dismissThresholdPx) onOpenChange(false)
  }

  const handleCopy = () => {
    if (copied) return
    navigator.clipboard?.writeText(code)
    setCopied(true)
    window.setTimeout(() => setCopied(false), copyTimeoutMs)
  }

  const rawLines = code.split("\n")
  const lineCount = tokenizedLines?.length ?? rawLines.length

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        className={cn(
          "v1 w-full p-0 gap-0 overflow-hidden flex flex-col",
          isMobile && "h-dvh max-h-dvh pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]",
          !isMobile && "transition-[max-width] duration-300 ease-out",
          !isMobile && (expanded ? "sm:max-w-none" : "sm:max-w-3xl"),
          className,
        )}
        showCloseButton={false}
      >
        {isMobile && (
          <button
            type="button"
            onPointerDown={onHandleDown}
            onPointerUp={onHandleUp}
            onPointerCancel={onHandleUp}
            aria-label={grabHandleAriaLabel}
            className="shrink-0 w-full pt-2 pb-1.5 flex items-center justify-center cursor-grab active:cursor-grabbing touch-none group"
          >
            <span className="w-9 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-muted-foreground/60 group-active:bg-muted-foreground/80 transition-colors" />
          </button>
        )}
        <SheetHeader className="px-4 sm:px-5 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b border-border flex-row items-center justify-between gap-2 sm:gap-3 shrink-0">
          <div className="min-w-0 flex flex-col gap-0.5">
            <SheetTitle className="text-section text-foreground truncate">{title}</SheetTitle>
            {subtitle && (
              <SheetDescription className="text-caption text-muted-foreground truncate">
                {subtitle}
              </SheetDescription>
            )}
          </div>
          <div className="flex items-center gap-0.5 sm:gap-1.5 shrink-0">
            {actionsSlot}
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 sm:h-8 sm:w-8 p-0"
              onClick={handleCopy}
              aria-label={copied ? copiedAriaLabel : copyAriaLabel}
            >
              {copied ? (
                <Check size={14} strokeWidth={2} className="text-emerald-500" />
              ) : (
                <Copy size={14} strokeWidth={1.75} />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="hidden sm:inline-flex h-8 w-8 p-0"
              onClick={() => setExpanded((v) => !v)}
              aria-label={expanded ? collapseAriaLabel : expandAriaLabel}
            >
              {expanded ? (
                <Minimize2 size={14} strokeWidth={1.75} />
              ) : (
                <Maximize2 size={14} strokeWidth={1.75} />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 sm:h-8 sm:w-8 p-0"
              onClick={() => onOpenChange(false)}
              aria-label={closeAriaLabel}
            >
              <X size={16} strokeWidth={1.75} />
            </Button>
          </div>
        </SheetHeader>

        {bannerSlot}

        <div className="flex-1 overflow-auto min-h-0">
          {bodySlot ?? (
            <table className="border-collapse w-full font-mono text-body">
              <tbody>
                {Array.from({ length: lineCount }).map((_, i) => (
                  <tr key={i}>
                    <td className="select-none text-right text-caption text-muted-foreground/40 px-3 align-top border-r border-border whitespace-nowrap w-12">
                      {i + 1}
                    </td>
                    <td className="px-4 align-top whitespace-pre">
                      {tokenizedLines
                        ? tokenizedLines[i]?.map((t, j) => (
                            <span
                              key={j}
                              className="text-[color:var(--tok)]"
                              style={{ ["--tok" as string]: t.color ?? "inherit" }}
                            >
                              {t.content}
                            </span>
                          ))
                        : rawLines[i]}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {footerSlot}
      </SheetContent>
    </Sheet>
  )
}
